import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress,
  Alert, Chip, Divider, Button,
} from '@mui/material';
import {
  AssignmentOutlined as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';

/**
 * "Mis evaluaciones": tests (DISC, razonamiento/habilidades personales, ventas)
 * enviados al email del candidato, a través de todas las organizaciones y
 * cargos a los que postuló. El backend resuelve el join por el email de la
 * sesión (GET /public/candidates/me/tests).
 *
 * Rendir un test abre el mismo link público del correo, con ?email= para que
 * la pantalla pública no vuelva a pedirlo.
 */
const PortalMyTestsScreen = ({ candidateData, sessionToken }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('en') ? 'en-US' : 'es-CL';
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTests = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/public/candidates/me/tests`,
          { headers: { 'x-candidate-token': sessionToken } }
        );

        if (response.data.response?.status_response === 'OK') {
          setTests(response.data.response.data || []);
        } else {
          setError(t('portal.myTests.loadError'));
        }
      } catch (err) {
        console.error(err);
        setError(t('portal.myTests.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (sessionToken) loadTests();
  }, [sessionToken]);

  const formatDate = (isoDate) => {
    if (!isoDate) return '—';
    const date = new Date(isoDate);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(dateLocale);
  };

  // Nombre visible del test según tipo
  const getTestLabel = (test) => {
    if (test.testKind === 'disc') return t('portal.myTests.kindDisc');
    if (test.testKind === 'sales') return t('portal.myTests.kindSales');
    return t(`portal.myTests.types.${test.testType}`, { defaultValue: test.testType });
  };

  const getStatusChip = (test) => {
    if (test.status === 'started') {
      return { label: t('portal.myTests.statusStarted'), color: 'primary' };
    }
    if (test.actionable) {
      return { label: t('portal.myTests.statusPending'), color: 'primary' };
    }
    if (test.status === 'expired') {
      return { label: t('portal.myTests.statusExpired'), color: 'default' };
    }
    return { label: t('portal.myTests.statusCompleted'), color: 'default' };
  };

  // El link público valida contra el email del test; se pasa por query para
  // que la pantalla pública lo pre-llene y no lo vuelva a pedir
  const buildTestUrl = (test) => {
    const email = candidateData?.email;
    if (!email) return test.link;
    const separator = test.link.includes('?') ? '&' : '?';
    return `${test.link}${separator}email=${encodeURIComponent(email)}`;
  };

  const pendingTests = tests.filter((test) => test.actionable);
  const finishedTests = tests.filter((test) => !test.actionable);

  const renderTest = (test, i, actionable) => {
    const statusChip = getStatusChip(test);
    return (
      <Box key={`${test.testKind}-${test.id}`}>
        {i > 0 && <Divider />}
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600}>
              {getTestLabel(test)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {test.positionName || t('portal.myTests.unknownPosition')}
              {test.organizationName ? ` · ${test.organizationName}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('portal.myTests.sentOn')} {formatDate(test.sentAt || test.createdAt)}
            </Typography>
            {actionable && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                endIcon={<OpenInNewIcon fontSize="small" />}
                href={buildTestUrl(test)}
                target="_blank"
                rel="noopener"
                sx={{ mt: 1.5, textTransform: 'none' }}
              >
                {test.status === 'started'
                  ? t('portal.myTests.continue')
                  : t('portal.myTests.start')}
              </Button>
            )}
          </Box>
          <Chip
            label={statusChip.label}
            color={statusChip.color}
            size="small"
            variant={statusChip.color === 'primary' ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 2, py: 4 }}>

      {/* Header — logo centrado como en las otras pantallas del portal */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          component="img"
          src="/amandalogoh.png"
          alt="Amanda HR"
          sx={{ height: 40, mb: 3 }}
        />
        <Typography variant="h5" fontWeight={700}>
          {t('portal.myTests.title')}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error">{error}</Alert>
      )}

      {!loading && !error && tests.length === 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 4, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {t('portal.myTests.empty')}
          </Typography>
        </Paper>
      )}

      {!loading && !error && pendingTests.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('portal.myTests.pendingSection')}
          </Typography>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            {pendingTests.map((test, i) => renderTest(test, i, true))}
          </Paper>
        </>
      )}

      {!loading && !error && finishedTests.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('portal.myTests.finishedSection')}
          </Typography>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
            {finishedTests.map((test, i) => renderTest(test, i, false))}
          </Paper>
        </>
      )}

    </Box>
  );
};

export default PortalMyTestsScreen;
